class VideoSpotlightSlider extends HTMLElement {
    constructor() {
        super();
        this._activeIndex = 0;
        this._slides = [];
        this._infoPanels = [];
        this._lastDirection = 1;
        this._modalObservers = [];
        this._animatingTimer = null;
        this._endedHandlers = new Map();
        this._touchStartX = 0;
        this._touchStartY = 0;
        this._touchIsHorizontal = null;
        this._wideMedia = window.matchMedia('(min-width: 750px)');
        this._handleViewportChange = this._handleViewportChange.bind(this);
        this._handleSectionLoad = this._handleSectionLoad.bind(this);
        this._handleBlockSelect = this._handleBlockSelect.bind(this);
        this._handleTouchStart = this._handleTouchStart.bind(this);
        this._handleTouchMove = this._handleTouchMove.bind(this);
        this._handleTouchEnd = this._handleTouchEnd.bind(this);
    }

    connectedCallback() {
        this._sectionId = this.dataset.sectionId;
        this._track = this.querySelector('.vs-track');
        this._trackOuter = this.querySelector('.vs-track-outer');
        this._prevBtn = this.querySelector('[name="previous"]');
        this._nextBtn = this.querySelector('[name="next"]');
        this._slides = Array.from(this.querySelectorAll('.vs-slide'));
        this._infoPanels = Array.from(this.querySelectorAll('.vs-info-panel'));

        if (!this._track || !this._slides.length) return;

        this.dataset.slideCount = String(this._slides.length);
        this._updateLayoutMode();
        this._bindEvents();
        if (this.dataset.aboveTheFold === 'true') this._skipInitialAutoplay = true;
        this._goToSlide(0, false, 1);
        this._skipInitialAutoplay = false;
        this._setupModalAutoPlay();
    }

    disconnectedCallback() {
        this._prevBtn?.removeEventListener('click', this._handlePrevClick);
        this._nextBtn?.removeEventListener('click', this._handleNextClick);

        this._slides.forEach((slide, i) => {
            if (this._slideClickHandlers?.[i]) {
                slide.removeEventListener('click', this._slideClickHandlers[i]);
            }
        });

        this._trackOuter?.removeEventListener('touchstart', this._handleTouchStart);
        this._trackOuter?.removeEventListener('touchmove', this._handleTouchMove);
        this._trackOuter?.removeEventListener('touchend', this._handleTouchEnd);

        if (typeof this._wideMedia.removeEventListener === 'function') {
            this._wideMedia.removeEventListener('change', this._handleViewportChange);
        } else {
            this._wideMedia.removeListener(this._handleViewportChange);
        }

        document.removeEventListener('shopify:section:load', this._handleSectionLoad);
        document.removeEventListener('shopify:block:select', this._handleBlockSelect);

        clearTimeout(this._animatingTimer);
        this._modalObservers.forEach((observer) => observer.disconnect());
        this._modalObservers = [];
    }

    _bindEvents() {
        this._handlePrevClick = () => this._navigate(-1);
        this._handleNextClick = () => this._navigate(1);

        this._prevBtn?.addEventListener('click', this._handlePrevClick);
        this._nextBtn?.addEventListener('click', this._handleNextClick);

        this._slideClickHandlers = this._slides.map((slide, i) => {
            const handler = () => {
                const index = parseInt(slide.dataset.index, 10);
                if (!Number.isNaN(index) && index !== this._activeIndex) {
                    this._goToSlide(index, true, this._getNavigationDirection(index));
                }
            };
            slide.addEventListener('click', handler);
            slide.addEventListener('keydown', (e) => {
                if (e.target !== slide) return;
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handler();
                }
            });
            return handler;
        });

        if (this._trackOuter) {
            this._trackOuter.addEventListener('touchstart', this._handleTouchStart, { passive: true });
            this._trackOuter.addEventListener('touchmove', this._handleTouchMove, { passive: false });
            this._trackOuter.addEventListener('touchend', this._handleTouchEnd, { passive: true });
        }

        if (typeof this._wideMedia.addEventListener === 'function') {
            this._wideMedia.addEventListener('change', this._handleViewportChange);
        } else {
            this._wideMedia.addListener(this._handleViewportChange);
        }

        document.addEventListener('shopify:section:load', this._handleSectionLoad);
        document.addEventListener('shopify:block:select', this._handleBlockSelect);
    }

    _handleTouchStart(e) {
        this._touchStartX = e.touches[0].clientX;
        this._touchStartY = e.touches[0].clientY;
        this._touchIsHorizontal = null;
    }

    _handleTouchMove(e) {
        if (this._touchIsHorizontal === null) {
            const dx = Math.abs(e.touches[0].clientX - this._touchStartX);
            const dy = Math.abs(e.touches[0].clientY - this._touchStartY);
            this._touchIsHorizontal = dx > dy;
        }
        if (this._touchIsHorizontal) {
            e.preventDefault();
        }
    }

    _handleTouchEnd(e) {
        if (!this._touchIsHorizontal) return;
        const delta = e.changedTouches[0].clientX - this._touchStartX;
        if (Math.abs(delta) < 40) return;
        this._navigate(delta < 0 ? 1 : -1);
    }

    _handleViewportChange() {
        this._updateLayoutMode();
        this._applySlideState(false);
    }

    _handleSectionLoad(event) {
        if (event.detail.sectionId === this._sectionId) {
            this._updateLayoutMode();
            this._goToSlide(0, false, 1);
        }
    }

    _handleBlockSelect(event) {
        const block = event.target.closest('.vs-slide');
        if (!block || !this.contains(block)) return;

        const index = this._slides.indexOf(block);
        if (index > -1) {
            this._goToSlide(index, true, this._getNavigationDirection(index));
        }
    }

    _updateLayoutMode() {
        this.dataset.layout = this._wideMedia.matches ? 'wide' : 'mobile';
    }

    _navigate(direction) {
        if (this._slides.length < 2) return;
        const nextIndex = (this._activeIndex + direction + this._slides.length) % this._slides.length;
        this._goToSlide(nextIndex, true, direction);
    }

    _goToSlide(index, animate = true, direction = 1) {
        const outgoingSlide = this._slides[this._activeIndex];
        if (outgoingSlide) {
            const outgoingVideo = outgoingSlide.querySelector('video');
            if (outgoingVideo) {
                outgoingVideo.pause();
                const endedHandler = this._endedHandlers.get(outgoingSlide);
                if (endedHandler) {
                    outgoingVideo.removeEventListener('ended', endedHandler);
                    this._endedHandlers.delete(outgoingSlide);
                }
            }
        }

        this._activeIndex = index;
        this._lastDirection = direction || this._lastDirection;

        if (animate) {
            this.classList.add('is-animating');
            clearTimeout(this._animatingTimer);
            this._animatingTimer = setTimeout(() => this.classList.remove('is-animating'), 560);
        }

        this._applySlideState(animate);
        this._activateVideo(index);
    }

    _applySlideState(animate = true) {
        if (!animate) {
            this.classList.add('is-static');
            requestAnimationFrame(() => this.classList.remove('is-static'));
        }

        this._slides.forEach((slide, index) => {
            const isActive = index === this._activeIndex;
            const slot = this._getSlotForIndex(index);

            slide.dataset.slot = slot;
            slide.classList.toggle('is-active', isActive);
            slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });

        this._infoPanels.forEach((panel, index) => {
            const isActive = index === this._activeIndex;
            panel.classList.toggle('is-active', isActive);
            panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });
    }

    _getSlotForIndex(index) {
        const count = this._slides.length;
        const isWide = this._wideMedia.matches;

        if (count === 1) return 'center';
        if (count === 2) return this._getTwoSlideSlot(index);

        const delta = this._getSignedDelta(index);
        if (delta === 0) return 'center';
        if (delta === -1) return 'left-1';
        if (delta === 1) return 'right-1';

        if (isWide) {
            if (delta === -2) return 'left-2';
            if (delta === 2) return 'right-2';
        }

        return 'hidden';
    }

    _getTwoSlideSlot(index) {
        if (index === this._activeIndex) return 'center';
        return this._activeIndex % 2 === 0 ? 'right-1' : 'left-1';
    }

    _getSignedDelta(index) {
        const count = this._slides.length;
        const forward = (index - this._activeIndex + count) % count;
        const backward = (this._activeIndex - index + count) % count;

        if (forward === 0) return 0;
        if (forward < backward) return forward;
        if (backward < forward) return -backward;

        return this._lastDirection > 0 ? -backward : forward;
    }

    _getNavigationDirection(targetIndex) {
        const count = this._slides.length;
        const forward = (targetIndex - this._activeIndex + count) % count;
        const backward = (this._activeIndex - targetIndex + count) % count;

        if (forward === backward) {
            return this._lastDirection > 0 ? 1 : -1;
        }

        return forward < backward ? 1 : -1;
    }

    _activateVideo(index) {
        if (this._skipInitialAutoplay) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        const slide = this._slides[index];
        if (!slide) return;

        const deferredMedia = slide.querySelector('deferred-media');
        if (!deferredMedia) return;

        const attachEndedHandler = (video) => {
            const endedHandler = () => this._navigate(1);
            this._endedHandlers.set(slide, endedHandler);
            video.addEventListener('ended', endedHandler);
        };

        if (!deferredMedia.hasAttribute('loaded')) {
            deferredMedia.loadContent(false);
            const video = slide.querySelector('video');
            if (video) {
                video.addEventListener('canplay', () => attachEndedHandler(video), { once: true });
            }
        } else {
            const video = slide.querySelector('video');
            if (video) {
                video.currentTime = 0;
                video.play().catch((err) => { if (err.name !== 'AbortError') console.warn('[VideoSpotlight] play() failed:', err); });
                attachEndedHandler(video);
            }
        }
    }

    _setupModalAutoPlay() {
        requestAnimationFrame(() => {
            this._slides.forEach((_, index) => {
                const modal = document.getElementById(`VideoModal-${this._sectionId}-${index + 1}`);
                if (!modal) return;

                const observer = new MutationObserver(() => {
                    const video = modal.querySelector('video');

                    if (modal.hasAttribute('open')) {
                        const activeSlideVideo = this._slides[this._activeIndex]?.querySelector('video');
                        activeSlideVideo?.pause();
                        requestAnimationFrame(() => {
                            if (video) {
                                video.currentTime = 0;
                                video.play().catch((err) => { if (err.name !== 'AbortError') console.warn('[VideoSpotlight] modal play() failed:', err); });
                            }
                        });
                    } else {
                        if (video) {
                            video.pause();
                            video.currentTime = 0;
                        }
                        this._activateVideo(this._activeIndex);
                    }
                });

                observer.observe(modal, { attributes: true, attributeFilter: ['open'] });
                this._modalObservers.push(observer);
            });
        });
    }
}

if (!customElements.get('video-spotlight-slider')) {
    customElements.define('video-spotlight-slider', VideoSpotlightSlider);
}
